import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import CmsTabs from '../../../components/admin/CmsTabs';
import { formatApiError } from '../../../lib/apiError';
import { useToast } from '../../../hooks/use-toast';
import axios from 'axios';
import {
  FileText, Plus, Pencil, Trash2, Loader2, X, AlertCircle,
  Copy, Send, Undo2, Archive, Search, ChevronLeft, ChevronRight, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  published: { label: 'Published', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  archived: { label: 'Archived', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};
const VIS_CONFIG = {
  public: { label: 'Public', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  private: { label: 'Private', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  hidden: { label: 'Hidden', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const PageModal = ({ isOpen, onClose, page, onSave }) => {
  const [form, setForm] = useState({
    slug: '', title: '', status: 'draft', visibility: 'private',
    order: 0, locale: 'en', content: '{}', meta: '{}'
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (page) {
      setForm({
        slug: page.slug, title: page.title, status: page.status,
        visibility: page.visibility, order: page.order, locale: page.locale,
        content: JSON.stringify(page.content || {}, null, 2),
        meta: JSON.stringify(page.meta || {}, null, 2),
      });
    } else {
      setForm({ slug: '', title: '', status: 'draft', visibility: 'private', order: 0, locale: 'en', content: '{}', meta: '{}' });
    }
    setError('');
  }, [page, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    let parsedContent, parsedMeta;
    try { parsedContent = JSON.parse(form.content || '{}'); } catch { setError('Invalid JSON in Content'); setSaving(false); return; }
    try { parsedMeta = JSON.parse(form.meta || '{}'); } catch { setError('Invalid JSON in Meta'); setSaving(false); return; }
    const payload = {
      slug: form.slug, title: form.title, status: form.status, visibility: form.visibility,
      order: Number(form.order) || 0, locale: form.locale, content: parsedContent, meta: parsedMeta,
    };
    if (page) payload.expected_updated_at = page.updated_at;
    try {
      if (page) await axios.put(`${API_URL}/api/admin/cms/pages/${page.id}`, payload);
      else await axios.post(`${API_URL}/api/admin/cms/pages`, payload);
      onSave(); onClose();
    } catch (err) {
      setError(formatApiError(err, 'Failed to save page'));
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800">
          <h2 className="text-xl font-semibold text-white">{page ? 'Edit Page' : 'Create Page'}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="cms-page-form">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="cms-page-form-error">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Title *</label>
              <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="cms-page-title-input"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Slug *</label>
              <input type="text" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="my-page-slug" data-testid="cms-page-slug-input"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 font-mono text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="cms-page-status-select"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Visibility</label>
              <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                data-testid="cms-page-visibility-select"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500">
                <option value="private">Private</option>
                <option value="public">Public</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Locale</label>
              <input type="text" value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}
                data-testid="cms-page-locale-input"
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Order</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
              data-testid="cms-page-order-input"
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Content (JSON)</label>
            <textarea rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              data-testid="cms-page-content-input"
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500 font-mono text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Meta (JSON)</label>
            <textarea rows={3} value={form.meta} onChange={(e) => setForm({ ...form, meta: e.target.value })}
              data-testid="cms-page-meta-input"
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500 font-mono text-sm" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid="cms-page-save-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <>{page ? 'Update' : 'Create'} Page</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CmsPages = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [visFilter, setVisFilter] = useState('');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (visFilter) params.visibility = visFilter;
      const res = await axios.get(`${API_URL}/api/admin/cms/pages`, { params });
      setPages(res.data);
    } catch (e) {
      console.error('Error fetching CMS pages:', e);
    } finally { setLoading(false); }
  }, [statusFilter, visFilter]);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  // Load default page size from CMS settings once (admin-only preference).
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/cms/settings`)
      .then((r) => { if (r.data?.default_page_size) setPageSize(r.data.default_page_size); })
      .catch(() => {});
  }, []);

  const handleAction = async (fn, id, successMsg) => {
    setBusyId(id);
    try {
      await fn();
      await fetchPages();
      if (successMsg) toast({ title: successMsg });
    } catch (e) {
      toast({ title: 'Action failed', description: formatApiError(e, 'Action failed'), variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const doPublish = (p) => handleAction(() => axios.post(`${API_URL}/api/admin/cms/pages/${p.id}/publish`), p.id, 'Page published');
  const doUnpublish = (p) => handleAction(() => axios.post(`${API_URL}/api/admin/cms/pages/${p.id}/unpublish`), p.id, 'Page unpublished');
  const doArchive = (p) => handleAction(() => axios.put(`${API_URL}/api/admin/cms/pages/${p.id}`, { status: 'archived' }), p.id, 'Page archived');
  const doDuplicate = (p) => handleAction(() => axios.post(`${API_URL}/api/admin/cms/pages/${p.id}/duplicate`), p.id, 'Page duplicated');
  const doDelete = (p) => {
    if (!window.confirm(`Delete page "${p.title}" and all its sections?`)) return;
    handleAction(() => axios.delete(`${API_URL}/api/admin/cms/pages/${p.id}`), p.id, 'Page deleted');
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '-');

  // client-side search + sort + pagination
  const filtered = pages
    .filter((p) => !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.slug.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      switch (sortBy) {
        case 'title_asc': return a.title.localeCompare(b.title);
        case 'title_desc': return b.title.localeCompare(a.title);
        case 'order_asc': return a.order - b.order;
        case 'updated_asc': return new Date(a.updated_at) - new Date(b.updated_at);
        default: return new Date(b.updated_at) - new Date(a.updated_at);
      }
    });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(pageNum, totalPages);
  const paginated = filtered.slice((current - 1) * pageSize, current * pageSize);
  useEffect(() => { setPageNum(1); }, [search, statusFilter, visFilter, sortBy]);

  return (
    <AdminLayout>
      <PageModal isOpen={showModal} onClose={() => setShowModal(false)} page={editing} onSave={fetchPages} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-testid="cms-pages-title">
          <FileText className="w-6 h-6 text-amber-400" /> CMS
        </h1>
        <p className="text-slate-400">Manage content pages, sections and CMS configuration.</p>
      </div>

      <CmsTabs />

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Pages</h2>
        <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="cms-create-page-btn"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors">
          <Plus className="w-5 h-5" /> Create Page
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder="Search title or slug..." value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="cms-pages-search"
            className="w-full pl-9 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="cms-pages-status-filter"
          className="px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <select value={visFilter} onChange={(e) => setVisFilter(e.target.value)} data-testid="cms-pages-visibility-filter"
          className="px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500">
          <option value="">All Visibility</option>
          <option value="public">Public</option>
          <option value="private">Private</option>
          <option value="hidden">Hidden</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="cms-pages-sort"
          className="px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500">
          <option value="updated_desc">Newest Updated</option>
          <option value="updated_asc">Oldest Updated</option>
          <option value="title_asc">Title A-Z</option>
          <option value="title_desc">Title Z-A</option>
          <option value="order_asc">Order</option>
        </select>
      </div>

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-12" data-testid="cms-pages-empty">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No pages found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cms-pages-table">
              <thead>
                <tr className="border-b border-slate-700">
                  {['Title', 'Status', 'Visibility', 'Locale', 'Version', 'Last Updated', 'Actions'].map((h) => (
                    <th key={h} className={`text-xs font-medium text-slate-400 uppercase tracking-wider px-5 py-4 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {paginated.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-700/20 transition-colors" data-testid={`cms-page-row-${p.id}`}>
                    <td className="px-5 py-4">
                      <span className="font-medium text-white">{p.title}</span>
                      <p className="text-xs text-slate-500 font-mono mt-1">/{p.slug}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${STATUS_CONFIG[p.status]?.color}`} data-testid={`cms-page-status-${p.id}`}>
                        {STATUS_CONFIG[p.status]?.label || p.status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${VIS_CONFIG[p.visibility]?.color}`}>
                        {VIS_CONFIG[p.visibility]?.label || p.visibility}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-300 uppercase">{p.locale}</td>
                    <td className="px-5 py-4 text-sm text-slate-300">v{p.version}</td>
                    <td className="px-5 py-4">
                      <span className="text-slate-400 text-sm">{formatDate(p.updated_at)}</span>
                      <p className="text-xs text-slate-500 mt-1">by {p.updated_by || '—'}</p>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {busyId === p.id && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
                        <button onClick={() => navigate(`/admin/cms/pages/${p.id}/edit`)} title="Edit" data-testid={`cms-page-edit-${p.id}`}
                          className="p-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => doDuplicate(p)} title="Duplicate" data-testid={`cms-page-duplicate-${p.id}`}
                          className="p-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"><Copy className="w-4 h-4" /></button>
                        {p.status !== 'published' ? (
                          <button onClick={() => doPublish(p)} title="Publish" data-testid={`cms-page-publish-${p.id}`}
                            className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors"><Send className="w-4 h-4" /></button>
                        ) : (
                          <button onClick={() => doUnpublish(p)} title="Unpublish" data-testid={`cms-page-unpublish-${p.id}`}
                            className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"><Undo2 className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => doArchive(p)} title="Archive" data-testid={`cms-page-archive-${p.id}`}
                          className="p-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors"><Archive className="w-4 h-4" /></button>
                        <button onClick={() => doDelete(p)} title="Delete" data-testid={`cms-page-delete-${p.id}`}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-400" data-testid="cms-pages-count">
            Showing {(current - 1) * pageSize + 1}-{Math.min(current * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <button disabled={current <= 1} onClick={() => setPageNum(current - 1)} data-testid="cms-pages-prev"
              className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 disabled:opacity-40 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-slate-300">Page {current} / {totalPages}</span>
            <button disabled={current >= totalPages} onClick={() => setPageNum(current + 1)} data-testid="cms-pages-next"
              className="p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 disabled:opacity-40 transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CmsPages;
